import React from 'react';
import { StarIcon, SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { Provider } from '../../../types/api.types';
import FeaturedProviderCardSkeleton from '../../../components/FeaturedProviderCardSkeleton';
import SafeImage from '../../../components/common/SafeImage';
import { useNavigate } from 'react-router-dom';
import { trackFeaturedClick } from '../../../services/featured.service';

interface FeaturedContentSectionProps {
  providers: Provider[];
  loading: boolean;
  onProviderClick: (provider: Provider) => void;
  onProviderBooking?: (provider: Provider) => void;
  city?: string | null;
}

// Featured Provider Card with prominent Featured badge
const FeaturedProviderCard: React.FC<{
  provider: Provider;
  onClick: () => void;
  onBookClick?: () => void;
  eagerLoad?: boolean;
}> = ({ provider, onClick, onBookClick, eagerLoad }) => {

  // Get the first location's city if available (handle both string and Location types)
  const firstLocation = provider.locations?.[0];
  const locationCity = firstLocation && typeof firstLocation !== 'string'
    ? firstLocation.address?.city
    : null;

  // Get category from provider (may be on the provider directly or in a custom field)
  const providerCategory = (provider as any).category;

  const handleClick = () => {
    // Track click for analytics
    trackFeaturedClick(provider._id);
    onClick();
  };

  const handleBookClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Track click for analytics
    trackFeaturedClick(provider._id);
    onBookClick?.();
  };

  return (
    <div
      className="group bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden border-2 border-accent-200 hover:border-accent-400 transition-all duration-300 hover:shadow-xl cursor-pointer relative"
      onClick={handleClick}
    >
      {/* Featured Badge */}
      <div className="absolute top-3 left-3 z-10">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-accent-600 to-indigo-600 text-white text-xs font-semibold rounded-full shadow-lg">
          <SparklesIcon className="w-3.5 h-3.5" />
          Featured
        </span>
      </div>

      {/* Image */}
      <div className="relative h-36 sm:h-48 overflow-hidden">
        <SafeImage
          src={provider.logo || provider.profileImage}
          fallbackSrc="/assets/default-provider.png"
          alt={provider.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          width={400}
          height={192}
          {...(eagerLoad ? { loading: 'eager' as const, fetchPriority: 'high' as const } : {})}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

        {/* Rating badge on image */}
        {provider.rating > 0 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-lg shadow-md">
            <StarIcon className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-bold text-gray-900">{provider.rating.toFixed(1)}</span>
            <span className="text-xs text-gray-500">({provider.totalReviews})</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 group-hover:text-accent-600 transition-colors line-clamp-1">
          {provider.name}
        </h3>

        {providerCategory && (
          <span className="inline-block text-xs font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full mb-2">
            {providerCategory.replace(/_/g, ' ')}
          </span>
        )}

        {locationCity && (
          <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
            <MapPinIcon className="w-4 h-4 text-gray-400" />
            <span className="line-clamp-1">{locationCity}</span>
          </div>
        )}

        {provider.bio && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{provider.bio}</p>
        )}

        {/* Book Now button */}
        <button
          onClick={handleBookClick}
          className="w-full py-2.5 px-4 bg-gradient-to-r from-accent-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-accent-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          Book Now
        </button>
      </div>
    </div>
  );
};

const FeaturedProvidersSection: React.FC<FeaturedContentSectionProps> = ({
  providers,
  loading,
  onProviderClick,
  onProviderBooking,
  city,
}) => {
  const navigate = useNavigate();

  // Filter featured providers or show first 3 if no featured flag yet
  const featuredProviders = providers.slice(0, 3);

  // Don't render if no featured providers and not loading
  if (!loading && featuredProviders.length === 0) {
    return null;
  }

  const handleViewAll = () => {
    navigate('/providers?featured=true');
  };

  return (
    <section className="py-10 sm:py-16 px-3 xs:px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-accent-50 to-white" id="featured-content">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <SparklesIcon className="w-6 h-6 sm:w-7 sm:h-7 text-accent-600" />
              <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-gray-900">
                Featured Providers{city ? ` Near ${city}` : ''}
              </h2>
            </div>
            <p className="text-sm sm:text-lg text-gray-600">
              Handpicked professionals delivering exceptional service
            </p>
          </div>
          {featuredProviders.length > 3 && (
            <button
              onClick={handleViewAll}
              className="flex items-center gap-1 text-accent-600 hover:text-accent-700 font-semibold text-xs sm:text-sm self-start sm:self-auto transition-colors"
            >
              View All Featured
              <ArrowRightIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          )}
        </div>

        {/* Featured Providers Grid */}
        {loading ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <FeaturedProviderCardSkeleton count={3} />
          </div>
        ) : (
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featuredProviders.map((provider, index) => (
              <FeaturedProviderCard
                key={provider._id}
                provider={provider}
                onClick={() => onProviderClick(provider)}
                onBookClick={() => onProviderBooking?.(provider)}
                eagerLoad={index < 3}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProvidersSection;
